import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SeoHead from '../../components/feature/SeoHead';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      return;
    }

    if (formData.message.length > 500) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d63di4hov5qgbon31tgg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'CONTACT | New Age Cinema',
    description: '映像作家 EIJI HOSHINOへのお問い合わせページ。映像制作のご依頼・ご相談はこちらからどうぞ。',
    url: `${siteUrl}/contact`,
    inLanguage: 'ja',
    mainEntity: {
      '@type': 'Person',
      name: 'EIJI HOSHINO',
      email: 'info@new-age-cinema.com',
    },
  };

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="CONTACT | New Age Cinema - EIJI HOSHINO お問い合わせ"
        description="映像作家 EIJI HOSHINOへのお問い合わせページ。映像制作のご依頼・ご相談はこちらからどうぞ。"
        keywords="お問い合わせ,映像制作依頼,EIJI HOSHINO,New Age Cinema,映像作家"
        canonical="/contact"
        jsonLd={jsonLd}
      />
      <Header />

      <main className="pt-24 md:pt-32 px-5 md:px-12 pb-20 md:pb-32">
        {/* Page Title */}
        <div className="max-w-3xl mx-auto mb-12 md:mb-24 text-center">
          <h1 className="text-5xl md:text-8xl font-light tracking-[0.2em] md:tracking-[0.3em] text-black mb-4 md:mb-6">CONTACT</h1>
          <p className="text-sm font-light text-gray-500">お気軽にお問い合わせください</p>
        </div>

        {/* Form */}
        <div className="max-w-2xl mx-auto">
          <form onSubmit={handleSubmit} data-readdy-form id="contact_form">
            <div className="space-y-10 md:space-y-16">
              {/* Name Field */}
              <div>
                <label htmlFor="name" className="block text-xs font-light tracking-[0.2em] text-[#00F0FF] mb-3 md:mb-4">
                  NAME
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full bg-transparent border-b border-gray-300 focus:border-[#00F0FF] outline-none py-3 text-sm md:text-base font-light text-black transition-colors duration-300"
                  required
                />
              </div>

              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-xs font-light tracking-[0.2em] text-[#00F0FF] mb-3 md:mb-4">
                  EMAIL
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full bg-transparent border-b border-gray-300 focus:border-[#00F0FF] outline-none py-3 text-sm md:text-base font-light text-black transition-colors duration-300"
                  required
                />
              </div>

              {/* Message Field */}
              <div>
                <label htmlFor="message" className="block text-xs font-light tracking-[0.2em] text-[#00F0FF] mb-3 md:mb-4">
                  MESSAGE
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={6}
                  maxLength={500}
                  className="w-full bg-transparent border-b border-gray-300 focus:border-[#00F0FF] outline-none py-3 text-sm md:text-base font-light text-black resize-none transition-colors duration-300"
                  required
                ></textarea>
                <p className="text-xs text-gray-400 mt-2 text-right">{formData.message.length} / 500</p>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-[#00F0FF] text-white px-8 md:px-12 py-4 md:py-5 text-sm font-light tracking-[0.2em] hover:bg-black transition-all duration-300 group flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'SENDING...' : 'SEND MESSAGE'}
                  <span className="inline-block transition-transform duration-300 group-hover:translate-x-2">→</span>
                </button>
              </div>
            </div>
          </form>

          {/* Status Messages */}
          {submitStatus === 'success' && (
            <div className="mt-8 p-5 md:p-6 border border-[#00F0FF] bg-[#00F0FF]/5">
              <p className="text-sm font-light text-center text-black">
                お問い合わせありがとうございます。<br />
                内容を確認次第、ご連絡させていただきます。
              </p>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mt-8 p-5 md:p-6 border border-red-500 bg-red-50">
              <p className="text-sm font-light text-center text-red-600">
                送信に失敗しました。<br />
                お手数ですが、もう一度お試しください。
              </p>
            </div>
          )}

          {/* Alternative Contact */}
          <div className="mt-16 md:mt-24 text-center">
            <p className="text-sm font-light text-gray-500 mb-4">または直接メールでご連絡ください</p>
            <a
              href="mailto:info@new-age-cinema.com"
              className="text-base md:text-lg font-mono text-[#00F0FF] hover:text-black transition-colors duration-300"
            >
              info@new-age-cinema.com
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
